import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import type { ElawContract } from '@/types/approval';
import {
  formatContractAmount,
  formatContractPeriod,
  formatContractRenewal,
  formatContractText,
} from './format';

const CELL_STYLE = {
  padding: '0 3px 0 10px',
  height: '40px',
  verticalAlign: 'middle',
  border: '1px solid #CCCCCC',
  textAlign: 'left',
} as const;
const LABEL_STYLE = {
  ...CELL_STYLE,
  padding: '0 3px',
  textAlign: 'center',
  backgroundColor: '#FCFCFC',
} as const;

const ContractSnapshotTable = ({
  contract,
  t,
}: {
  contract: ElawContract;
  t: TFunction;
}) => (
  <table
    className="__se_tbl"
    cellSpacing={0}
    cellPadding={0}
    style={{
      width: '100%',
      tableLayout: 'fixed',
      fontSize: '13px',
      marginBottom: '10px',
      borderCollapse: 'collapse',
    }}
  >
    <tbody>
      <tr>
        <td style={LABEL_STYLE}>{t('계약명')}</td>
        <td style={CELL_STYLE} colSpan={4}>
          {formatContractText(contract.contractName)}
        </td>
        <td style={LABEL_STYLE}>{t('신규/갱신')}</td>
        <td style={CELL_STYLE} colSpan={2}>
          {formatContractRenewal(contract.isNewContract, {
            renewal: t('신규'),
            extension: t('갱신'),
          })}
        </td>
      </tr>
      <tr>
        <td style={LABEL_STYLE}>{t('상대방')}</td>
        <td style={CELL_STYLE} colSpan={7}>
          {formatContractText(contract.opponentInfo)}
        </td>
      </tr>
      <tr>
        <td style={LABEL_STYLE}>{t('계약대금')}</td>
        <td style={CELL_STYLE} colSpan={7}>
          {formatContractAmount(contract.amount)}
        </td>
      </tr>
      <tr>
        <td style={LABEL_STYLE}>{t('계약기간')}</td>
        <td style={CELL_STYLE} colSpan={7}>
          {formatContractPeriod(contract.startDate, contract.expireDate)}
        </td>
      </tr>
    </tbody>
  </table>
);

/**
 * attributes.contractContent — 레거시 계약 표와 같은 모양(계약마다 표 1개).
 * 서버가 Oracle CL01 에 본문 앞에 붙여 저장해 레거시 화면이 이 HTML 을 그대로 보여줌.
 * 첨부파일 줄은 넣지 않음 — V2 는 다운로드 링크가 없음(PORTAL-15253)
 */
export const buildContractContentHtml = (
  contracts: ElawContract[],
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <>
      {contracts.map((contract) => (
        <ContractSnapshotTable
          key={contract.contractId}
          contract={contract}
          t={t}
        />
      ))}
    </>,
  );

const LEGACY_LABELS = {
  계약명: 'contractName',
  상대방: 'opponentInfo',
  계약대금: 'amount',
} as const;

const cellText = (cell: Element | null | undefined) =>
  cell?.textContent?.replace(/\s+/g, ' ').trim() || null;

/**
 * 레거시 문서 attributes.content(작성 시점 계약 표 HTML) → 계약 목록.
 * 값은 작성 시점 표기 그대로(금액 "100,000,000원", 기간 "2026/08/25 ~ …").
 * 첨부파일 칸의 다운로드 링크는 기한 없는 링크(auth_token=NO_LIMIT)라 그대로 씀.
 * 계약명이 빈 표는 레거시가 값을 못 채운 채 저장한 것이라 뺌
 */
export const parseLegacyContractSnapshot = (
  html: string | null | undefined,
  contractIds: number[],
): ElawContract[] => {
  if (!html || !html.includes('__se_tbl') || typeof window === 'undefined')
    return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // 계약 표(계약명 칸이 있는 표)만 — 다른 표가 섞여도 contractIds 순서와 맞게
  const tables = Array.from(doc.querySelectorAll('table')).filter((table) =>
    Array.from(table.querySelectorAll('td')).some(
      (cell) => cellText(cell) === '계약명',
    ),
  );
  return tables.flatMap((table, index): ElawContract[] => {
    // 추가 계약은 표를 감싼 상자 바로 아래 삭제 버튼 ID. 기준 계약 표는 최상위라 상자가 없음
    const holder = table.parentElement;
    const deleteId =
      holder &&
      holder !== doc.body &&
      holder.querySelectorAll('table').length === 1
        ? Number(holder.querySelector(':scope > button.delete')?.id)
        : NaN;
    const contract: ElawContract = {
      contractId: deleteId || contractIds[index] || -(index + 1),
    };
    table.querySelectorAll('td').forEach((cell) => {
      const label = cellText(cell);
      const valueCell = cell.nextElementSibling;
      if (!label || !valueCell) return;
      if (label in LEGACY_LABELS) {
        contract[LEGACY_LABELS[label as keyof typeof LEGACY_LABELS]] =
          cellText(valueCell);
      } else if (label === '신규/갱신') {
        const value = cellText(valueCell);
        contract.isNewContract =
          value === '신규' ? true : value === '갱신' ? false : null;
      } else if (label === '계약기간') {
        const [start, expire] = (cellText(valueCell) ?? '').split('~');
        contract.startDate = start?.trim() || null;
        contract.expireDate = expire?.trim() || null;
      } else if (label === '첨부파일') {
        contract.downloadUrl =
          valueCell.querySelector('a[href]')?.getAttribute('href') ?? null;
      }
    });
    return contract.contractName ? [contract] : [];
  });
};
